import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import CharacterCounter from '@/components/Common/CharacterCounter.vue';

import { merge } from 'rxjs';
import { filter, auditTime, map, debounceTime } from 'rxjs/operators';
import { resizeInput } from '@util';
import debounce from 'lodash/debounce';

const valuePath = R.path(['target', 'value']);

export default {
    name: 'InputField',
    mounted() {
        this.$nextTick(() => this.watchValue$());

        var value$ = this.$watchAsObservable('_value', {
            immediate: !this.isLimitLine,
        });

        var resize$ = this.$watchAsObservable('windowDimension').pipe(
            debounceTime(300)
        );

        const merged$ = merge(value$, resize$).pipe(
            auditTime(150),
            map(() => this.$refs.elem),
            filter((el) => el && this.type === 'textarea')
        );

        this.$subscribeTo(merged$, resizeInput);

        this.isLimitLine && this.setTextareaStyle(false);
        if (this.autofocus) {
            const elem = this.$refs.elem;
            elem && elem.focus();
        }
    },
    props: {
        value: {
            type: [String, Number],
            default: '',
        },
        type: {
            type: String,
            default: 'input',
        },
        placeholder: String,
        limit: {
            type: Number | String,
        },
        error: {
            type: String,
            default: null,
        },
        resizable: {
            type: Boolean,
            default: true,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        additionalClass: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        isLimitLine: {
            type: Boolean,
            default: false,
        },
        headerText: {
            type: String,
        },
        cleaner: {
            type: Function,
            default: R.identity,
        },
        autocorrect: {
            type: String,
            default: 'on',
        },
        autocapitalize: {
            type: String,
            default: 'none',
        },
        counter: Function,
        charCounter: Function,
        liftPlaceholder: {
            type: Boolean,
            default: true,
        },
        customErrorContent: {
            type: Object,
            default: undefined,
        },
        inputType: {
            type: String,
            default: 'text',
        },
    },
    data() {
        return {
            focused: false,
        };
    },
    render() {
        const {
            limit,
            _value,
            placeholder = '',
            className = '',
            type,
            autofocus,
            disabled,
            focused,
            error,
            placeholderPosition,
            autocorrect,
            autocapitalize,
            inputType,
        } = this;

        let props = {
            attrs: {
                class: className,
                autofocus,
                placeholder: placeholderPosition === 'bottom' && placeholder,
                disabled,
                autocorrect,
                autocapitalize,
                type: inputType,
            },
            ref: 'elem',
            on: {
                focus: () => {
                    this.focused = true;
                    this.$emit('focus');
                    this.isLimitLine && this.setTextareaStyle(true);
                },
                blur: () => {
                    this.focused = false;
                    this.isLimitLine && this.setTextareaStyle(false);
                },
                keydown: (e) => this.$emit('keydown', e),
                keypress: (e) => this.$emit('keypress', e),
                keyup: (e) => this.$emit('keyup', e),
                change: (e) => this.$emit('change', e),
                click: (e) => this.$emit('click', e),
                mousedown: (e) => this.$emit('mousedown', e),
            },
            directives: [
                {
                    name: 'maxchars',
                    value: limit,
                    modifiers: R.pick(['counter', 'charCounter'], this),
                },
            ],
        };

        let classes = ['adriel-field-wrapper', this.additionalClass];
        limit && classes.push('has-limit');
        focused && classes.push('focused');
        type === 'textarea' && classes.push('textarea-wrapper');
        error && classes.push('adriel-field-has-error');

        if (placeholderPosition === 'top') {
            classes.push('placeholder-top');
        } else if (placeholderPosition === 'bottom') {
            classes.push('placeholder-top-remove');
        }

        if (type !== 'input') {
            props = R.assocPath(
                ['on', 'keydown'],
                (e) => {
                    this.$emit('keydown', e);

                    // ko v-model bug workaround
                    e.key === 'Backspace' && this.handleText();
                },
                props
            );
        }

        const elem =
            type === 'input' ? (
                <input
                    {...props}
                    value={this._value}
                    onInput={(e) => (this._value = valuePath(e))}
                />
            ) : (
                <textarea {...props} v-model={this._value} />
            );

        let errorElem = '';

        if (error) {
            let content = (
                <button>
                    <i class="fas fa-map-marker-exclamation" />
                </button>
            );

            if (this.customErrorContent) {
                content = this.customErrorContent;
            }

            errorElem = this.wrapTooltip({
                visible: true,
                content,
                title: error,
                placement: 'bottom',
            });
        }

        return (
            <span class={classes.join(' ')}>
                {this.liftPlaceholder && (
                    <span class="header-text">{placeholder}</span>
                )}
                {this.$slots.icon}
                {elem}
                {errorElem}
                {limit && (
                    <CharacterCounter
                        str={_value}
                        max={limit}
                        counter={this.counter}
                    />
                )}
                {this.$slots.last}
                {this.loading && <ClipLoader color="#999" size="20px" />}
            </span>
        );
    },
    methods: {
        //resize input
        watchValue$() {
            let value$ = this.$watchAsObservable('_value', {
                immediate: true,
                deep: true,
            }).pipe(
                auditTime(200),
                filter(() => this.resizable && this.type !== 'textarea')
            );

            this.$subscribeTo(value$, () => {
                const span = document.createElement('span');
                this.className
                    .split(' ')
                    .forEach((name) => span.classList.add(name));

                span.classList.add('adriel-span-for-input');

                span.innerText = this.value;
                document.body.appendChild(span);

                this.$refs.elem.style.width = span.offsetWidth + 'px';
                span.parentNode.removeChild(span);
            });
        },
        focus() {
            const elem = this.$refs.elem;
            elem && elem.focus();
        },
        setTextareaStyle(isFocus = false) {
            const elem = this.$refs.elem;
            let cList = elem.classList;

            cList.add('input-view');
            if (isFocus) {
                cList.add('onFocus');
                cList.remove('onBlur');
                resizeInput(elem, 5);
            } else {
                cList.remove('onFocus');
                cList.add('onBlur');
            }
        },
        handleText: debounce(function () {
            const el$ = this.$el.querySelector('textarea');

            if (!el$) {
                return;
            }

            if (this._value !== el$.value) {
                this._value = el$.value;
            }
        }, 200),
    },
    computed: {
        _value: {
            get: R.compose(R.defaultTo(''), R.prop('value')),
            set(value) {
                const refined = this.cleaner(value);
                this.$emit('input', refined);

                if (refined !== value) {
                    this.$forceUpdate();
                }
            },
        },
        className() {
            let classes = [`adriel-field-${this.type}`];
            this.limit && classes.push('has-limit');
            return classes.join(' ');
        },
        placeholderPosition() {
            return String(this._value).trim() ? 'top' : 'bottom';
        },
    },
};
